import { TGS_AVATAR_BASE_URL } from "./constants";

const removeUndefinedValues = (value) =>
  Object.entries(value).reduce((result, [key, item]) => {
    if (item !== undefined && item !== null) {
      result[key] = item;
    }

    return result;
  }, {});

const getDateOnly = (value) => {
  if (typeof value !== "string") return value;

  return value.split("T")[0];
};

const getAvatarId = (profile = {}) =>
  profile["avatar-id"] || profile.avatarId;

const findAvatarId = (...profiles) =>
  profiles.map((profile) => profile?.["avatar-id"]).find(Boolean) ||
  profiles.map((profile) => profile?.avatarId).find(Boolean) ||
  "";

const mapWebCustomerProfile = (profile = {}) =>
  removeUndefinedValues({
    "customer-number": profile.customerNumber,
    "first-name": profile.firstName,
    "last-name": profile.lastName,
    dob: getDateOnly(profile.birthDate),
    email: profile.email,
    gender: profile.gender,
    cellphone: profile.phoneNumber,
    address: profile.address,
    "speeding-amount": profile.promotionAmount,
    "coin-amount": profile.gCoinAmountDouble ?? profile.gCoinAmount,
    "gpp-card-number": profile.customerNumber,
    "gpp-magnetic-number": profile.magneticCardNumber,
    "full-name": profile.fullName,
    "avatar-id": getAvatarId(profile),
  });

const mapAppCustomerProfile = (profile = {}, userId) =>
  removeUndefinedValues({
    id: userId,
    gender: profile.gender,
    birthDate: profile.dob,
    firstName: profile["first-name"],
    lastName: profile["last-name"],
    fullName: profile["full-name"],
    phoneNumber: profile.cellphone,
    email: profile.email,
    customerNumber: profile["customer-number"],
    address: profile.address,
    gCoinAmount: profile["coin-amount"],
    gCoinAmountDouble: profile["coin-amount"],
    promotionAmount: profile["speeding-amount"],
    magneticCardNumber: profile["gpp-magnetic-number"],
    avatarId: getAvatarId(profile),
  });

export const buildTgsAvatarUrl = (
  avatarId,
  baseUrl = TGS_AVATAR_BASE_URL,
) => {
  const normalizedAvatarId = String(avatarId || "").trim();
  if (!normalizedAvatarId) return "";
  if (/^https?:\/\//i.test(normalizedAvatarId)) return normalizedAvatarId;

  const normalizedBaseUrl = String(baseUrl || "").trim().replace(/\/+$/, "");
  if (!normalizedBaseUrl) return "";

  const encodedAvatarId = encodeURIComponent(normalizedAvatarId);
  if (normalizedBaseUrl.includes("imagedelivery.net")) {
    return `${normalizedBaseUrl}/${encodedAvatarId}/public`;
  }

  return `${normalizedBaseUrl}/files/${encodedAvatarId}`;
};

/**
 * Normalizes the direct web login and App Golden Gate callback responses into
 * one shape. The original API fields are retained for backward compatibility.
 */
export const normalizeTgsLoginData = (data = {}, guid = "") => {
  const webCustomerProfile = data?.["customer-profile"] || {};
  const appCustomerProfile = data?.["customer-profile-for-s-o"] || {};
  const userId =
    data?.["user-id"] || data?.userId || webCustomerProfile?.id || "";
  const avatarId = findAvatarId(
    webCustomerProfile,
    appCustomerProfile,
    data,
  );
  const avatar = buildTgsAvatarUrl(avatarId);
  const customerProfileForQrOrder = Object.keys(appCustomerProfile).length
    ? appCustomerProfile
    : mapWebCustomerProfile(webCustomerProfile);
  const customerProfile = Object.keys(webCustomerProfile).length
    ? webCustomerProfile
    : mapAppCustomerProfile(appCustomerProfile, userId);

  return {
    ...data,
    ...customerProfileForQrOrder,
    ...(Object.keys(customerProfile).length
      ? { "customer-profile": customerProfile }
      : {}),
    ...(Object.keys(customerProfileForQrOrder).length
      ? { "customer-profile-for-s-o": customerProfileForQrOrder }
      : {}),
    ...(userId ? { "user-id": userId, userId } : {}),
    ...(avatarId ? { avatarId, avatar } : {}),
    guid,
  };
};
